import {getPublishedCollection} from '@wix/music-shared/dist/src/getPublishedCollection';
import {TAlbum} from '../../../typings/todo';
import {BI_ERRORS} from '../biErrorLogger/biErrors';
import {getMetaSiteId} from '../env/getMetaSiteId/getMetaSiteId';
import {getIsTestMode} from '../env/isTestMode';
import {errorLogger} from '../errorLogger/errorLogger';
import {ALBUM_TYPES, ENTITY_TYPES, ENTITY_TYPES_MAP} from './albumProviderConstant';
import {filterOutInvalidExternalsUrls} from './filterOutInvalidExternalUrls';

export const getEntityType = (album, track?) => {
  if (track) {
    return ENTITY_TYPES.TRACK;
  }

  return ENTITY_TYPES_MAP[album.albumType] || ENTITY_TYPES.ALBUM;
};

export const isEntityTrack = (album, track?) => {
  return getEntityType(album, track) === ENTITY_TYPES.TRACK;
};

export const isAlbumPlaylist = (album) => {
  return album.albumType === ALBUM_TYPES.PLAYLIST;
};

export const getExternalLinks = (album, track?) => {
  return (
    (isEntityTrack(album, track) && album.albumType === ALBUM_TYPES.PLAYLIST
      ? track.trackExternalLinks
      : album.albumExternalLinks) || []
  );
};

export const isExternalLinksEnabled = (album, track?) => {
  const {isAlbumExternalLinksEnabled} = album;

  return Boolean(
    isEntityTrack(album, track)
      ? (isAlbumExternalLinksEnabled || isAlbumPlaylist(album)) && track.isTrackExternalLinksEnabled
      : isAlbumExternalLinksEnabled,
  );
};

export const getEntityTitle = (album, track?) => {
  return isEntityTrack(album, track) ? track.title : album.title;
};

export const isPurchaseAvail = (album, track?) => {
  return isEntityTrack(album, track) ? track.purchaseAvail : album.purchaseAvail;
};

const isMinimalPrice = (entity) => Boolean(entity.isMinimalAlbumPrice || entity.isMinimalTrackPrice);

export const isSubscribeRequired = (entity) =>
  entity.purchaseAvail && entity.isSubscriptionRequired && !isMinimalPrice(entity);
export const isNameYourPrice = (entity) => entity.purchaseAvail && isMinimalPrice(entity);

export const isFreeTrack = (track) =>
  track.purchaseAvail && Number(track.trackPrice) === 0 && !isNameYourPrice(track) && !isSubscribeRequired(track);

export const isFreeAlbum = (album) =>
  album.purchaseAvail && Number(album.albumPrice) === 0 && !isNameYourPrice(album) && !isSubscribeRequired(album);

export const isFreeEntity = (album, track) => (track ? isFreeTrack(track) : isFreeAlbum(album));

export const isBuy = (entity) =>
  entity.purchaseAvail &&
  Number(entity.trackPrice || entity.albumPrice) > 0 &&
  !isMinimalPrice(entity) &&
  !isSubscribeRequired(entity);

const COLLECTIONS_WIXSTATIC = 'https://collections.wixstatic.com/';
const TEST_COLLECTIONS_WIXSTATIC = 'http://localhost:3102/';

export const transformCollection = ({collection}) => {
  const trackData = ({id, title, public_properties: publicProperties}) => ({
    id,
    title,
    ...publicProperties,
  });
  return {
    id: collection.id,
    title: collection.title,
    tracks: collection.items.map(trackData),
    ...collection.public_properties,
  };
};

export function fetchAlbumData(albumId): Promise<{album: TAlbum}> {
  const baseUrl = getIsTestMode() ? TEST_COLLECTIONS_WIXSTATIC : COLLECTIONS_WIXSTATIC;
  return getPublishedCollection({
    id: albumId,
    metaSiteId: getMetaSiteId(),
    baseUrl,
  })
    .then((collection) => {
      const album = filterOutInvalidExternalsUrls(transformCollection({collection}));
      return {album};
    })
    .catch(
      errorLogger.createLoggingCatcher({
        bi: BI_ERRORS.getWindow.album.load,
      }),
    );
}
